import { inject, injectable } from "../injection";
import {
    observable,
    action,
    reaction,
    runInAction,
    when,
    makeObservable
} from "mobx";
import { MessageBusService } from "../messageBus/MessageBusService";
import { AngularLoaderStore } from "../bridgeSetup/AngularLoaderStore";
import { windowAsAny } from "../windowAsAny/windowAsAny";

@injectable("singleton")
export class LoggedInUserStore {
    @observable public isReady = false;

    @observable public id = -1;
    @observable public username = "";
    @observable public forename = "";
    @observable public surname = "";
    @observable public displayName = "";
    @observable public emailAddress = "";

    @observable public isImpersonating = false;
    @observable.shallow
    private permissions: RedDeer.Contracts.Enums.PermissionType[] = [];
    @observable.shallow
    private bundles: RedDeer.Contracts.Enums.PermissionBundle[] = [];
    @observable.shallow
    private products: RedDeer.Contracts.Enums.ProductType[] = [];
    @observable.shallow
    public splits?: RedDeer.Contracts.Dtos.User.LoggedInUserSplits;

    @observable public enableUsageAnalytics = false;
    @observable public clientName = "";
    @observable
    public jobTitleType: RedDeer.Contracts.Enums.UserJobTitleType | null = null;

    @observable public isMiddleOfficeUser = false;
    @observable public enableInteractionReview = false;
    @observable public enableMifidReadAccessForSurveillance = false;
    @observable public evaluationIsActive = false;
    @observable public bettermodeDontShowLegalInfoAgain = false;
    public bettermodeOpenInNewTab = true;

    constructor(
        @inject(MessageBusService)
        private messageBusService: MessageBusService,
        @inject(AngularLoaderStore)
        private angularLoaderStore: AngularLoaderStore
    ) {
        makeObservable(this);

        void this.getData();
        this.watchForActiveEvaluations();
    }

    private async getData() {
        // get permissions from initial payload, if exists
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (windowAsAny().redDeerInitialPayload) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const initialPayload: RedDeer.Contracts.Dtos.ReactApp.InitialPayloadDto =
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                windowAsAny().redDeerInitialPayload;
            if (initialPayload.LoggedInUser) {
                this.setData(initialPayload.LoggedInUser);
                return;
            }
        }

        // otherwise, get from message bus
        const query: RedDeer.Contracts.Queries.User.GetLoggedInUserQuery = {
            MessageType: "Queries.User.GetLoggedInUserQuery"
        };

        const response =
            await this.messageBusService.asyncSendPromise<RedDeer.Contracts.Queries.User.GetLoggedInUserResponse>(
                {
                    request: query,
                    description: "getting user permissions"
                }
            );

        if (response.Result) {
            this.setData(response.Result);
        }
    }

    @action
    private setData(data: RedDeer.Contracts.Dtos.User.LoggedInUserDto) {
        this.isReady = true;

        this.id = data.UserDto?.Id ?? this.id;
        this.username = data.Username ?? this.username;
        this.forename = data.UserDto?.Forename ?? this.forename;
        this.surname = data.UserDto?.Surname ?? this.surname;
        this.displayName = data.UserDto?.DisplayName ?? this.displayName;
        this.emailAddress = data.EmailAddress ?? this.emailAddress;

        this.isImpersonating = !!data.IsImpersonating;
        this.permissions = data.Permissions ?? this.permissions;
        this.bundles = data.Bundles ?? this.bundles;
        this.products = data.Products ?? this.products;
        this.splits = data.Splits ?? this.splits;

        this.enableUsageAnalytics = !!data.EnableUsageAnalytics;
        this.clientName = data.ClientName ?? this.clientName;
        this.jobTitleType = data.JobTitleType ?? this.jobTitleType;

        this.isMiddleOfficeUser = !!data.IsMiddleOfficeUser;
        this.enableInteractionReview = !!data.EnableInteractionReview;
        this.enableMifidReadAccessForSurveillance =
            !!data.EnableMifidReadAccessForSurveillance;
        this.evaluationIsActive = !!data.EvaluationIsActive;
        this.bettermodeDontShowLegalInfoAgain =
            !!data.BettermodeDontShowLegalInfoAgain;
    }

    @action
    public setBettermodeDontShowLegalInfoAgain = (value: boolean) => {
        this.bettermodeDontShowLegalInfoAgain = value;

        const request: RedDeer.Contracts.Commands.Bettermode.SetBettermodeDontShowLegalInfoAgainCommand =
            {
                MessageType:
                    "Commands.Bettermode.SetBettermodeDontShowLegalInfoAgainCommand",
                Value: value
            };
        this.messageBusService.send({
            request,
            description: "Setting community legal info settings",
            callback: () => undefined
        });
    };

    private watchForActiveEvaluations(): void {
        const check = async () => {
            const request: RedDeer.Contracts.Queries.Mifid.GetCurrentUserValuationQuery =
                {
                    MessageType: "Queries.Mifid.GetCurrentUserValuationQuery"
                };
            try {
                const response =
                    await this.messageBusService.asyncSendPromise<RedDeer.Contracts.Queries.Mifid.GetCurrentUserValuationResponse>(
                        {
                            request,
                            description: "getting current user evaluation"
                        }
                    );
                runInAction(() => {
                    this.evaluationIsActive = response.IsInProgress ?? false;
                });
            } catch {
                // we don't care if it errors, just need the response boolean
            }
        };

        // interval timer
        let interval: NodeJS.Timeout | undefined;
        reaction(
            () =>
                this.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
                ),
            canAccessMifid => {
                if (interval) {
                    clearInterval(interval);
                    interval = undefined;
                }
                if (canAccessMifid) {
                    interval = setInterval(() => {
                        void check();
                    }, 1800000); // check every 30 mins if broker vote is open
                }
            },
            {
                fireImmediately: true
            }
        );

        // watch angular scope
        void (async () => {
            await when(() => this.angularLoaderStore.hasLoaded);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            windowAsAny()
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                .RedDeer.UI.App.getInject("$rootScope")
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                .$on("mifidBrokerVoteChange", check);
        })();
    }

    public hasPermission(
        permission: RedDeer.Contracts.Enums.PermissionType
    ): boolean {
        return this.permissions.includes(permission);
    }

    public hasBundle(
        bundle: RedDeer.Contracts.Enums.PermissionBundle
    ): boolean {
        return this.bundles.includes(bundle);
    }

    public hasProduct(product: RedDeer.Contracts.Enums.ProductType): boolean {
        return this.products.includes(product);
    }
}
